import { Injectable } from '@angular/core';

import { PendoService } from '@patient-ui/patient-web/store';

import { FindALabTrackingContextService } from './find-a-lab-tracking-context.service';
import {
  FindALabSearchEventInput,
  FindALabSearchFilters,
  FindALabSearchParamsKey,
  LocateMeLocation,
  buildFindALabSearchEventFromParams,
  computeDaysBetween,
  computePreferredDateMetrics,
  deriveSearchMethod,
  isDuplicateFindALabSearchParamsKey,
  resolveSearchZip,
  serializeFindALabSearchParamsKey,
  slotDateString,
} from '../utils/find-a-lab-search-event.util';

export interface FindALabSearchOnLoadInput {
  paramsKey: FindALabSearchParamsKey;
  searchInput: FindALabSearchEventInput;
  filters: FindALabSearchFilters;
  lastLocateMe: LocateMeLocation | null;
  todayDateString: string;
}

export interface FindALabStandardSlotHoldInput {
  locationId: number;
  slot: string;
  serviceId: number;
}

@Injectable({ providedIn: 'root' })
export class FindALabResultsTrackingService {
  private lastTrackedSearchParamsKey: string | null = null;
  private lastSearchInput: FindALabSearchEventInput | null = null;
  private lastFilters: FindALabSearchFilters | null = null;
  private lastLocateMe: LocateMeLocation | null = null;
  private lastPreferredDate: string | null = null;

  constructor(
    private readonly pendoService: PendoService,
    private readonly findALabTrackingContext: FindALabTrackingContextService
  ) {}

  trackSearchOnLoad(input: FindALabSearchOnLoadInput): void {
    const paramsKey = serializeFindALabSearchParamsKey(input.paramsKey);
    if (
      isDuplicateFindALabSearchParamsKey(
        paramsKey,
        this.lastTrackedSearchParamsKey
      )
    ) {
      return;
    }
    this.lastTrackedSearchParamsKey = paramsKey;
    this.lastSearchInput = input.searchInput;
    this.lastFilters = input.filters;
    this.lastLocateMe = input.lastLocateMe;
    this.lastPreferredDate = input.paramsKey.date;

    const searchMethod = deriveSearchMethod(
      input.searchInput.addressZip,
      input.searchInput.latitude,
      input.searchInput.longitude,
      input.lastLocateMe
    );

    this.pendoService.trackFindALabSearch(
      buildFindALabSearchEventFromParams({
        searchInput: input.searchInput,
        filters: input.filters,
        searchMethod,
        preferredDate: input.paramsKey.date,
        todayDateString: input.todayDateString,
      })
    );
  }

  trackStandardSlotHold(input: FindALabStandardSlotHoldInput): void {
    if (!this.lastSearchInput || !this.lastFilters || !this.lastPreferredDate) {
      return;
    }

    const searchMethod = deriveSearchMethod(
      this.lastSearchInput.addressZip,
      this.lastSearchInput.latitude,
      this.lastSearchInput.longitude,
      this.lastLocateMe
    );
    const dateMetrics = computePreferredDateMetrics(
      this.lastPreferredDate,
      this.getTodayLocalDate()
    );
    const zip = resolveSearchZip(this.lastSearchInput);
    const lat = this.lastSearchInput.latitude ?? undefined;
    const lon = this.lastSearchInput.longitude ?? undefined;

    this.findALabTrackingContext.save({
      preferred_date: this.lastPreferredDate,
      search_method: searchMethod,
      ...(zip && { zip }),
    });

    this.pendoService.trackFindALabSlotSelected({
      locationId: input.locationId,
      slot: input.slot,
      service: input.serviceId,
      preferred_date: this.lastPreferredDate,
      search_method: searchMethod,
      ...dateMetrics,
      days_from_preferred_to_slot: computeDaysBetween(
        this.lastPreferredDate,
        slotDateString(input.slot)
      ),
      ...(zip && { zip }),
      ...(lat != null && lat !== 0 && { lat }),
      ...(lon != null && lon !== 0 && { lon }),
    });
  }

  private getTodayLocalDate(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
